import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, TrendingUp, TrendingDown, DollarSign, Percent, BarChart3,
  RefreshCw, Info, ChevronDown, Layers
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, ComposedChart, Bar, ScatterChart, Scatter
} from 'recharts';

function BusinessOutcomesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [correlations, setCorrelations] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [lagAnalysis, setLagAnalysis] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedMetric) {
      loadChartData(selectedMetric.id);
    }
  }, [selectedMetric]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [metricsRes, correlationsRes, summaryRes] = await Promise.all([
        axios.get('/business-metrics/config'),
        axios.get('/business-metrics/analysis/correlations'),
        axios.get('/business-metrics/analysis/summary')
      ]);
      
      setMetrics(metricsRes.data.metrics || []);
      setCorrelations(correlationsRes.data.correlations || []);
      setSummary(summaryRes.data);
      
      // Auto-select first metric
      if (metricsRes.data.metrics?.length > 0) {
        setSelectedMetric(metricsRes.data.metrics[0]);
      }
    } catch (err) {
      console.error('Failed to load business outcomes data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadChartData = async (metricId) => {
    try {
      const [chartRes, lagRes] = await Promise.all([
        axios.get(`/business-metrics/chart/overlay?metric_id=${metricId}`),
        axios.get(`/business-metrics/analysis/lag?metric_id=${metricId}&max_lag=90&step=7`).catch(() => null)
      ]);
      
      setChartData(chartRes.data);
      if (lagRes?.data) {
        setLagAnalysis(lagRes.data);
      }
    } catch (err) {
      console.error('Failed to load chart data:', err);
    }
  };

  const getCorrelationColor = (r) => {
    const absR = Math.abs(r);
    if (absR >= 0.7) return r > 0 ? 'text-green-600' : 'text-red-600';
    if (absR >= 0.5) return r > 0 ? 'text-green-500' : 'text-red-500';
    if (absR >= 0.3) return r > 0 ? 'text-yellow-600' : 'text-orange-500';
    return 'text-gray-500';
  };

  const getCorrelationBg = (r) => {
    const absR = Math.abs(r);
    if (absR >= 0.7) return r > 0 ? 'bg-green-100' : 'bg-red-100';
    if (absR >= 0.5) return r > 0 ? 'bg-green-50' : 'bg-red-50';
    return 'bg-gray-50';
  };

  const formatValue = (value, type, currency = 'USD') => {
    if (value === null || value === undefined) return '-';
    
    if (type === 'currency') {
      if (value >= 1000000000) {
        return `$${(value / 1000000000).toFixed(1)}B`;
      }
      if (value >= 1000000) {
        return `$${(value / 1000000).toFixed(1)}M`;
      }
      if (value >= 1000) {
        return `$${(value / 1000).toFixed(1)}K`;
      }
      return `$${value.toFixed(0)}`;
    }
    if (type === 'percentage') {
      return `${value.toFixed(1)}%`;
    }
    return value.toFixed(1);
  };

  const prepareChartData = () => {
    if (!chartData) return [];
    
    return chartData.time_periods.map((period, idx) => ({
      period: period,
      displayPeriod: formatPeriod(period),
      trustScore: chartData.trust_scores[idx],
      businessValue: chartData.business_values[idx]
    }));
  };

  const formatPeriod = (period) => {
    const [year, month] = period.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(month) - 1]} ${year.slice(2)}`;
  };

  const getMetricIcon = (type) => {
    switch (type) {
      case 'currency': return <DollarSign className="w-4 h-4" />;
      case 'percentage': return <Percent className="w-4 h-4" />;
      default: return <BarChart3 className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-purple-600 mx-auto mb-2" />
          <p className="text-gray-600">Loading business outcomes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard-v2')}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900" data-testid="outcomes-title">
                  Business Outcomes
                </h1>
                <p className="text-sm text-gray-500">Trust Score correlation with business metrics</p>
              </div>
            </div>
            <button
              onClick={loadInitialData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
                <Layers className="w-4 h-4" />
                Business Metrics
              </div>
              <p className="text-3xl font-bold text-gray-900">{summary.total_metrics}</p>
              <p className="text-sm text-gray-500 mt-1">Active metrics tracked</p>
            </div>
            
            {summary.strongest_positive && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 shadow-sm border border-green-100">
                <div className="flex items-center gap-2 text-green-600 text-sm mb-2">
                  <TrendingUp className="w-4 h-4" />
                  Strongest Positive
                </div>
                <p className="text-xl font-bold text-gray-900">{summary.strongest_positive.business_metric}</p>
                <p className="text-2xl font-bold text-green-600">
                  r = {summary.strongest_positive.correlation.toFixed(3)}
                </p>
                <p className="text-sm text-gray-600 mt-1">{summary.strongest_positive.interpretation}</p>
              </div>
            )}
            
            {summary.strongest_negative && (
              <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-5 shadow-sm border border-red-100">
                <div className="flex items-center gap-2 text-red-600 text-sm mb-2">
                  <TrendingDown className="w-4 h-4" />
                  Strongest Negative
                </div>
                <p className="text-xl font-bold text-gray-900">{summary.strongest_negative.business_metric}</p>
                <p className="text-2xl font-bold text-red-600">
                  r = {summary.strongest_negative.correlation.toFixed(3)}
                </p>
                <p className="text-sm text-gray-600 mt-1">{summary.strongest_negative.interpretation}</p>
              </div>
            )}
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Metric Selector */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-4 border-b border-gray-100">
                <h2 className="font-semibold text-gray-900">Business Metrics</h2>
                <p className="text-sm text-gray-500">Select to view correlation</p>
              </div>
              <div className="max-h-[500px] overflow-y-auto">
                {metrics.map((metric) => {
                  const correlation = correlations.find(c => c.business_metric_id === metric.id);
                  return (
                    <button
                      key={metric.id}
                      onClick={() => setSelectedMetric(metric)}
                      className={`w-full p-4 border-b border-gray-50 hover:bg-gray-50 transition text-left ${
                        selectedMetric?.id === metric.id ? 'bg-purple-50 border-l-4 border-l-purple-500' : ''
                      }`}
                      data-testid={`metric-${metric.name}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded-lg ${selectedMetric?.id === metric.id ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-500'}`}>
                            {getMetricIcon(metric.type)}
                          </div>
                          <span className="font-medium text-gray-900 text-sm">{metric.display_name}</span>
                        </div>
                        {correlation && (
                          <span className={`text-sm font-bold ${getCorrelationColor(correlation.correlation)}`}>
                            {correlation.correlation.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Correlation Legend */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 mt-4 p-4">
              <h3 className="font-medium text-gray-900 text-sm mb-3 flex items-center gap-2">
                <Info className="w-4 h-4 text-gray-400" />
                Correlation Guide
              </h3>
              <div className="space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Very Strong</span>
                  <span className="text-green-600 font-medium">0.9 - 1.0</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Strong</span>
                  <span className="text-green-500 font-medium">0.7 - 0.9</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Moderate</span>
                  <span className="text-yellow-600 font-medium">0.5 - 0.7</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Weak</span>
                  <span className="text-gray-500 font-medium">0.3 - 0.5</span>
                </div>
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="lg:col-span-3 space-y-6">
            {/* Dual-Axis Chart */}
            {chartData && selectedMetric && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Trust Score vs {chartData.business_metric_name}
                    </h2>
                    <p className="text-sm text-gray-500">Overlay comparison over time</p>
                  </div>
                  {correlations.find(c => c.business_metric_id === selectedMetric.id) && (
                    <div className={`px-4 py-2 rounded-lg ${getCorrelationBg(correlations.find(c => c.business_metric_id === selectedMetric.id).correlation)}`}>
                      <p className="text-xs text-gray-500">Correlation</p>
                      <p className={`text-xl font-bold ${getCorrelationColor(correlations.find(c => c.business_metric_id === selectedMetric.id).correlation)}`}>
                        r = {correlations.find(c => c.business_metric_id === selectedMetric.id).correlation.toFixed(3)}
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={prepareChartData()} margin={{ top: 10, right: 50, left: 10, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="displayPeriod" 
                        tick={{ fontSize: 11 }}
                        tickMargin={8}
                      />
                      <YAxis 
                        yAxisId="left"
                        domain={[0, 100]}
                        tick={{ fontSize: 11 }}
                        label={{ value: 'Trust Score', angle: -90, position: 'insideLeft', fontSize: 12 }}
                      />
                      <YAxis 
                        yAxisId="right"
                        orientation="right"
                        tick={{ fontSize: 11 }}
                        tickFormatter={(value) => formatValue(value, chartData.business_metric_type, chartData.currency)}
                        label={{ value: chartData.business_metric_name, angle: 90, position: 'insideRight', fontSize: 12 }}
                      />
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                                <p className="font-medium text-gray-900 mb-2">{label}</p>
                                {payload.map((entry, idx) => (
                                  <p key={idx} className="text-sm" style={{ color: entry.color }}>
                                    {entry.name}: {entry.name === 'Trust Score' 
                                      ? entry.value?.toFixed(1) 
                                      : formatValue(entry.value, chartData.business_metric_type, chartData.currency)
                                    }
                                  </p>
                                ))}
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Legend />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="trustScore"
                        name="Trust Score"
                        stroke="#8b5cf6"
                        strokeWidth={2}
                        dot={{ fill: '#8b5cf6', r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="businessValue"
                        name={chartData.business_metric_name}
                        stroke={selectedMetric.color || '#22c55e'}
                        strokeWidth={2}
                        dot={{ fill: selectedMetric.color || '#22c55e', r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Lag Analysis */}
            {lagAnalysis && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Lag Analysis</h2>
                    <p className="text-sm text-gray-500">
                      Optimal lag: <span className="font-medium text-purple-600">{lagAnalysis.optimal_lag_description}</span>
                      {' '}(r = {lagAnalysis.optimal_correlation.toFixed(3)})
                    </p>
                  </div>
                </div>
                
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart 
                      data={lagAnalysis.correlations_by_lag} 
                      margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="lag_description" 
                        tick={{ fontSize: 10 }}
                      />
                      <YAxis 
                        domain={[-1, 1]}
                        tick={{ fontSize: 11 }}
                        tickFormatter={(v) => v.toFixed(1)}
                      />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload[0]) {
                            const data = payload[0].payload;
                            return (
                              <div className="bg-white p-2 border border-gray-200 rounded shadow-lg text-sm">
                                <p className="font-medium">{data.lag_description}</p>
                                <p>Correlation: {data.correlation.toFixed(3)}</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar 
                        dataKey="correlation" 
                        fill="#8b5cf6"
                        radius={[4, 4, 0, 0]}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                  <p className="text-sm text-purple-800">
                    <strong>Insight:</strong> Trust Score changes appear to {lagAnalysis.optimal_lag_days > 0 
                      ? `predict ${lagAnalysis.business_metric} changes by approximately ${lagAnalysis.optimal_lag_days} days.`
                      : `correlate with ${lagAnalysis.business_metric} changes immediately.`
                    }
                  </p>
                </div>
              </div>
            )}

            {/* Correlation Matrix */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">All Correlations</h2>
              <div className="space-y-2">
                {correlations.map((corr) => (
                  <div 
                    key={corr.business_metric_id}
                    className={`flex items-center justify-between p-3 rounded-lg ${getCorrelationBg(corr.correlation)}`}
                  >
                    <div>
                      <p className="font-medium text-gray-900">{corr.business_metric}</p>
                      <p className="text-xs text-gray-500">{corr.interpretation}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-bold ${getCorrelationColor(corr.correlation)}`}>
                        {corr.correlation.toFixed(3)}
                      </p>
                      <p className="text-xs text-gray-500">{corr.significance}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default BusinessOutcomesPage;
